import { v4 as uuidv4 } from 'uuid';
import { bimKernel } from './BIMKernel';
import { BUILTIN_PROJECT_TEMPLATES, ProjectTemplateSpec, unitSettingsFor } from '../config/projectTemplates';
import { View, Level, Phase } from './types';

/**
 * Project Template Manager
 *
 * BIM-style template architecture: every new document is derived from a
 * template that carries levels, views, phases and unit settings instead of
 * starting from an empty sheet. Two template sources exist:
 *
 * - Built-in specs (src/config/projectTemplates.ts) instantiated through
 *   the BIM kernel, so they always match the current document model.
 * - Custom templates saved by the user ("Save Project as Template") and
 *   persisted in localStorage, importable/exportable as .yate files.
 */

export interface TemplateSummary {
    id: string;
    name: string;
    description: string;
    discipline: string;
    unitSystem: 'Metric' | 'Imperial';
    source: 'builtin' | 'custom';
    levelNames: string[];
    createdAt?: string;
}

interface StoredCustomTemplate {
    id: string;
    name: string;
    description: string;
    discipline: string;
    unitSystem: 'Metric' | 'Imperial';
    createdAt: string;
    levelNames: string[];
    /** Serialized kernel project snapshot (project template file). */
    projectJson: string;
}

const CUSTOM_TEMPLATES_KEY = 'yap.custom-templates.v1';

// --- Built-in templates ---

export function getBuiltinTemplateSummaries(): TemplateSummary[] {
    return BUILTIN_PROJECT_TEMPLATES.map(toBuiltinSummary);
}

function toBuiltinSummary(spec: ProjectTemplateSpec): TemplateSummary {
    return {
        id: spec.id,
        name: spec.name,
        description: spec.description,
        discipline: spec.discipline,
        unitSystem: spec.unitSystem,
        source: 'builtin',
        levelNames: spec.levels.map((l) => l.name),
    };
}

// --- Custom templates (localStorage library) ---

function getStoredCustomTemplates(): StoredCustomTemplate[] {
    try {
        const raw = localStorage.getItem(CUSTOM_TEMPLATES_KEY);
        return raw ? (JSON.parse(raw) as StoredCustomTemplate[]) : [];
    } catch {
        return [];
    }
}

function persistCustomTemplates(templates: StoredCustomTemplate[]): void {
    localStorage.setItem(CUSTOM_TEMPLATES_KEY, JSON.stringify(templates));
}

export function getCustomTemplateSummaries(): TemplateSummary[] {
    return getStoredCustomTemplates().map(toCustomSummary);
}

function toCustomSummary(stored: StoredCustomTemplate): TemplateSummary {
    return {
        id: stored.id,
        name: stored.name,
        description: stored.description,
        discipline: stored.discipline,
        unitSystem: stored.unitSystem,
        source: 'custom',
        levelNames: stored.levelNames,
        createdAt: stored.createdAt,
    };
}

/** All templates available in the New Project dialog, built-ins first. */
export function getAllTemplateSummaries(): TemplateSummary[] {
    return [...getBuiltinTemplateSummaries(), ...getCustomTemplateSummaries()];
}

/**
 * Serializes a fresh document derived from the given template. For built-in
 * templates the kernel instantiates the spec; for custom templates the
 * stored snapshot is reused with the new document name applied.
 */
export async function buildProjectJsonFromTemplate(templateId: string, docName: string): Promise<string> {
    const builtin = BUILTIN_PROJECT_TEMPLATES.find((t) => t.id === templateId);
    if (builtin) {
        bimKernel.newDocument(docName);
        applySpecToCurrentProject(builtin);
        return bimKernel.saveProject();
    }

    const stored = getStoredCustomTemplates().find((t) => t.id === templateId);
    if (!stored) {
        throw new Error(`Unknown project template: ${templateId}`);
    }
    return applyDocumentName(stored.projectJson, docName);
}

/**
 * Reshapes the kernel's fresh document to match a built-in template spec:
 * levels, one plan view per level, a {3D} view, construction phases and
 * project units (the contents a built-in project template ships with).
 */
function applySpecToCurrentProject(spec: ProjectTemplateSpec): void {
    const project = bimKernel.getProject();

    const levels = new Map<string, Level>();
    for (const levelSpec of spec.levels) {
        const id = uuidv4();
        levels.set(id, {
            id,
            name: levelSpec.name,
            elevation: levelSpec.elevation,
            isStory: levelSpec.isBuildingStory,
        });
    }
    project.levels = levels;

    const views = new Map<string, View>();
    const view3dId = uuidv4();
    views.set(view3dId, create3DView());
    let firstPlanId = '';
    for (const [levelId, level] of levels) {
        const planId = uuidv4();
        views.set(planId, createPlanView(level.name, levelId));
        if (!firstPlanId) firstPlanId = planId;
    }
    project.views = views;
    project.activeViewId = firstPlanId || view3dId;

    const phases: Phase[] = spec.phases.map((name, index) => ({ id: uuidv4(), name, sequence: index + 1 }));
    project.phases = phases;
    const activePhase = phases.find((p) => p.name === 'New Construction') ?? phases[phases.length - 1];
    project.activePhaseId = activePhase.id;

    project.unitSettings = unitSettingsFor(spec.unitSystem);
    project.number = spec.numberPrefix;
    project.grids = new Map();
    project.sheets = new Map();
    project.schedules = new Map();
    project.families = new Map();
    project.globalParameters = new Map();
    project.designOptionSets = [];
    project.revisions = [];
    project.customTitleBlocks = [];
}

function createPlanView(name: string, levelId: string): View {
    return {
        id: uuidv4(),
        name,
        type: 'FloorPlan',
        scale: 100,
        detailLevel: 'Coarse',
        visibility: {},
        filters: [],
        associatedLevelId: levelId,
        viewRange: {
            top: 4.0,
            cutPlane: 1.2,
            bottom: 0,
            depth: 1.0,
        },
        camera: {
            position: [0, 0, 100],
            target: [0, 0, 0],
            projection: 'Orthographic',
        },
    };
}

function create3DView(): View {
    return {
        id: uuidv4(),
        name: '{3D}',
        type: '3D',
        scale: 1,
        detailLevel: 'Medium',
        visibility: {},
        filters: [],
        camera: {
            position: [20, 20, 20],
            target: [0, 0, 0],
            projection: 'Perspective',
        },
    };
}

/** Rewrites the document name inside a serialized project snapshot. */
function applyDocumentName(projectJson: string, docName: string): string {
    try {
        const parsed = JSON.parse(projectJson);
        parsed.name = docName;
        return JSON.stringify(parsed);
    } catch {
        return projectJson;
    }
}

// --- Save / manage custom templates ---

/**
 * Captures the current document as a reusable custom template (BIM:
 * File > Save As > Template). Returns the new library entry summary.
 */
export async function saveCurrentProjectAsTemplate(name: string, description: string): Promise<TemplateSummary> {
    const projectJson = await bimKernel.saveProject();
    const parsed = JSON.parse(projectJson);
    const levels = (parsed?.levels ?? []) as [string, Level][];
    const stored: StoredCustomTemplate = {
        id: `custom-${uuidv4()}`,
        name,
        description,
        discipline: 'Custom',
        unitSystem: parsed?.unitSettings?.system ?? 'Metric',
        createdAt: new Date().toISOString(),
        levelNames: levels.map(([, level]) => level.name),
        projectJson,
    };
    const all = getStoredCustomTemplates();
    all.push(stored);
    persistCustomTemplates(all);
    return toCustomSummary(stored);
}

export function deleteCustomTemplate(templateId: string): void {
    const all = getStoredCustomTemplates().filter((t) => t.id !== templateId);
    persistCustomTemplates(all);
}

/**
 * Registers a .yate file's contents into the custom template library.
 * Throws when the payload is not a valid serialized project.
 */
export function importTemplateFromJson(name: string, projectJson: string): TemplateSummary {
    const parsed = JSON.parse(projectJson);
    if (!parsed || !Array.isArray(parsed.elements) || !Array.isArray(parsed.levels)) {
        throw new Error('The file does not contain a Yap project template.');
    }
    const stored: StoredCustomTemplate = {
        id: `custom-${uuidv4()}`,
        name,
        description: 'Imported from a template file.',
        discipline: 'Custom',
        unitSystem: parsed.unitSettings?.system ?? 'Metric',
        createdAt: new Date().toISOString(),
        levelNames: (parsed.levels as [string, Level][]).map(([, level]) => level?.name ?? 'Level'),
        projectJson,
    };
    const all = getStoredCustomTemplates();
    all.push(stored);
    persistCustomTemplates(all);
    return toCustomSummary(stored);
}

/** Serialized snapshot of a custom template, for .yate export. */
export function getCustomTemplateJson(templateId: string): string | null {
    return getStoredCustomTemplates().find((t) => t.id === templateId)?.projectJson ?? null;
}
